import { call, delay, put, select, takeLatest } from 'typed-redux-saga'
import { errorHandler, loadersSlice, notifyMessageCreator } from 'Modules'
import { FormNames } from 'Constants/FormNames'
import { routerActions } from 'connected-react-router'
import { FrontUrls } from 'Constants/FrontUrls'
import { Deserialize } from 'cerialize'
import { authSelector } from 'Pages/auth'
import { Access } from 'Constants/FunctionAliases'
import {
	BgLimitRequest,
	bgLimitRequestApi,
	BgLimitRequestFull,
	BgLimitRequestNs,
	bgLimitRequestSelector,
	bgLimitRequestSlice,
	BgStatus,
	bgStatusUpdateClosed,
} from 'Pages/ibro'
import { RequestParams, ResponseCatalog } from 'Models'

function* getLimitRequestList(action: BgLimitRequestNs.GetLimitRequestList) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { params, clear, callback } = action.payload

		const paramsForApi = params || (yield* select(bgLimitRequestSelector.selectParams))

		const res = yield* call(bgLimitRequestApi.getLimitRequestItems, paramsForApi)
		const items = new ResponseCatalog<BgLimitRequest>(res)
		items.entries = items.entries.map((item) => Deserialize(item, BgLimitRequest))

		if (!clear && paramsForApi.page !== 1) {
			const oldItems = yield* select(bgLimitRequestSelector.selectLimitRequestList)
			items.entries = [...oldItems, ...items.entries]
		}
		yield* put(bgLimitRequestSlice.actions.setLimitRequestList(items))
		yield* put(bgLimitRequestSlice.actions.setLimitRequestMeta(res.meta))
		if (params) yield* put(bgLimitRequestSlice.actions.setRequestParams(params))
		callback?.()
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* createLimitRequest(action: BgLimitRequestNs.SendCreateLimitRequest) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const res = yield* call(bgLimitRequestApi.createLimitRequest, action.payload)
		yield* put(routerActions.push(FrontUrls.broker.limits_edit.replace(':id', String(res.id))))
		const requestParams = yield* select(bgLimitRequestSelector.selectParams)
		yield* put(
			bgLimitRequestSlice.actions.setRequestParams(
				Object.assign(new RequestParams(), requestParams, { page: 1 })
			)
		)
	} catch (error) {
		yield* call(errorHandler, error, FormNames.BROKER.CREATE_LIMIT)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* getLimitRequest(action: BgLimitRequestNs.GetLimitRequest) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const id = action.payload
		const items = yield* select(bgLimitRequestSelector.selectLimitRequestList)
		const userAliases = yield* select(authSelector.getUserAliases)
		const canUpdate = userAliases?.includes(Access.broker.limit_request_update)
		const currentLimitFromItems = items?.find((item) => item.id === id)
		let res
		if (currentLimitFromItems) {
			res =
				currentLimitFromItems.external_id && canUpdate
					? yield* call(bgLimitRequestApi.checkLimitRequestStatus, id)
					: yield* call(bgLimitRequestApi.getLimitRequest, id)
		} else {
			res = yield* call(bgLimitRequestApi.getLimitRequest, id)
			if (res.external_id && canUpdate) {
				yield* delay(1500) // необходима для получения результата запроса статуса
				res = yield* call(bgLimitRequestApi.checkLimitRequestStatus, id)
			}
		}

		const currentLimitRequest = yield* select(bgLimitRequestSelector.selectCurrentLimitRequest)
		if (!currentLimitRequest)
			yield* put(
				bgLimitRequestSlice.actions.setLimitRequest({ data: Deserialize(res, BgLimitRequestFull) })
			)
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* sendLimitRequest(action: BgLimitRequestNs.LimitRequestSendRequest) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const {
			data,
			params: { status },
		} = action.payload

		let bg
		if (status?.status_slug === BgStatus.NOVA) {
			bg = yield* call(bgLimitRequestApi.limitRequestCheckLimit, data.id, data.amount as string)
		}
		if (status?.status_slug === BgStatus.CL_CHECKED) {
			yield* call(bgLimitRequestApi.saveLimitRequest, data.id, data)
			bg = yield* call(bgLimitRequestApi.postLimitRequest, data.id)
		}
		if (!bgStatusUpdateClosed.includes(status?.status_slug)) {
			yield* call(bgLimitRequestApi.saveLimitRequest, data.id, data)
			bg = yield* call(bgLimitRequestApi.updateLimitRequest, data.id)
		}

		if (yield* select(bgLimitRequestSelector.selectBgMessage))
			yield* put(bgLimitRequestSlice.actions.clearBgMessage())
		yield* put(
			bgLimitRequestSlice.actions.setLimitRequest({
				data: Deserialize(bg, BgLimitRequestFull),
			})
		)
	} catch (error) {
		yield* call(errorHandler, error, FormNames.BROKER.LIMIT)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* saveLimitRequest(action: BgLimitRequestNs.SaveLimitRequest) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const limit = yield* call(bgLimitRequestApi.updateLimit, action.payload.data)
		const data = Deserialize(limit, BgLimitRequestFull)

		yield* put(bgLimitRequestSlice.actions.setLimitRequest({ data }))
		yield* call(notifyMessageCreator, 'common.save.success')
	} catch (error) {
		yield* call(errorHandler, error, FormNames.BROKER.LIMIT)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* updateLimit() {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const item = yield* select(bgLimitRequestSelector.selectCurrentLimitRequest)
		const { status, amount, id } = item
		let res: BgLimitRequestFull
		if (status.status_slug === BgStatus.CL_CHECKED) {
			res = yield* call(bgLimitRequestApi.limitRequestCheckLimit, id, amount)
		}
		if ([BgStatus.FILLING, BgStatus.REWORK].includes(status.status_slug)) {
			res = yield* call(bgLimitRequestApi.limitCurrentElement, id)
		}
		if (res)
			yield* put(
				bgLimitRequestSlice.actions.setLimitRequest({ data: Deserialize(res, BgLimitRequestFull) })
			)
	} catch (error) {
		yield* call(errorHandler, error, FormNames.BROKER.LIMIT)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

export function* limitRequestSaga() {
	yield* takeLatest(bgLimitRequestSlice.actions.createLimitRequest.type, createLimitRequest)
	yield* takeLatest(bgLimitRequestSlice.actions.getLimitRequestList.type, getLimitRequestList)
	yield* takeLatest(bgLimitRequestSlice.actions.getLimitRequest.type, getLimitRequest)
	yield* takeLatest(bgLimitRequestSlice.actions.limitRequestSendRequest.type, sendLimitRequest)
	yield* takeLatest(bgLimitRequestSlice.actions.limitSaveRequest.type, saveLimitRequest)
	yield* takeLatest(bgLimitRequestSlice.actions.updateLimit.type, updateLimit)
}
